import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../auth/SupabaseClient";
import {
  ArrowLeft,
  Edit,
  ChevronDown,
  Navigation,
  Map,
  Calendar,
  DollarSign,
  Truck,
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Save,
  X,
  PlusCircle,
  Trash2,
  Search,
} from "lucide-react";
import DeliveryAddress from "../../components/DeliveryAddress";
import MapComponent from "../../components/MapComponent";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
}

interface OrderProduct {
  id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  product: Product;
}

interface Order {
  id: string;
  order_number: string;
  client_id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  status: "En attente" | "En cours" | "Terminée" | "Expédié" | "Annulée";
  delivery_method: "Livraison" | "Envoi" | "Retrait";
  payment_status: "Non payé" | "Payé" | "Remboursé" | "En attente de paiement";
  payment_method:
    | "Carte de crédit"
    | "PayPal"
    | "Virement bancaire"
    | "Espèces"
    | "Revolut";
  total_amount: number;
  delivery_address: string;
  expected_delivery_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  products: OrderProduct[];
}

interface Client {
  id: string;
  name: string;
  email: string;
  telephone: string;
}

export default function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [editedOrder, setEditedOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const inputClass =
    "bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-gray-900 dark:text-white";

  useEffect(() => {
    fetchOrder();
    fetchAvailableProducts();
    fetchClients();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!id) {
        throw new Error("L'ID de la commande est manquant.");
      }

      console.log("Fetching order with ID:", id);

      // Récupération de la commande
      const { data: orderData, error: orderError } = await supabase
        .from("orders")
        .select(
          `
          *,
          clients (
            name,
            email,
            telephone
          )
        `
        )
        .eq("id", id)
        .single();

      if (orderError) {
        console.error(
          "Erreur lors de la récupération de la commande:",
          orderError
        );
        throw new Error(
          `Erreur lors de la récupération de la commande: ${orderError.message}`
        );
      }

      if (!orderData) {
        console.error("Aucune commande trouvée avec cet ID");
        throw new Error("Commande non trouvée");
      }

      console.log("Données de la commande récupérées:", orderData);

      // Récupération des produits de la commande
      const { data: orderProductsData, error: orderProductsError } =
        await supabase
          .from("order_products")
          .select(
            `
          id,
          quantity,
          unit_price,
          total_price,
          product_id,
          products (
            id,
            name,
            description,
            price
          )
        `
          )
          .eq("order_id", id);

      if (orderProductsError) {
        console.error(
          "Erreur lors de la récupération des produits de la commande:",
          orderProductsError
        );
        throw new Error(
          `Erreur lors de la récupération des produits de la commande: ${orderProductsError.message}`
        );
      }

      if (!orderProductsData) {
        console.warn("Aucun produit trouvé pour cette commande");
      }

      console.log("Produits de la commande récupérés:", orderProductsData);

      const orderWithDetails: Order = {
        id: orderData.id,
        order_number: orderData.order_number,
        client_id: orderData.client_id,
        client_name: orderData.clients?.name ?? "Client inconnu",
        client_email: orderData.clients?.email ?? "Email non disponible",
        client_phone:
          orderData.clients?.telephone ?? "Téléphone non disponible",
        status: orderData.status,
        delivery_method: orderData.delivery_method,
        payment_status: orderData.payment_status,
        payment_method: orderData.payment_method,
        total_amount:
          orderProductsData?.reduce(
            (total, op) => total + op.quantity * (op.products as any).price,
            0
          ) ?? 0,
        delivery_address: orderData.delivery_address,
        expected_delivery_date: orderData.expected_delivery_date,
        notes: orderData.notes,
        created_at: orderData.created_at,
        updated_at: orderData.updated_at,
        products:
          orderProductsData?.map((op) => {
            const productData = op.products as unknown as {
              id: string;
              name: string;
              description: string;
              price: number;
            };
            console.log("Product data:", productData);
            return {
              id: op.id,
              product_id: op.product_id,
              quantity: op.quantity,
              unit_price: op.unit_price,
              total_price: op.total_price,
              product: productData
                ? {
                    id: productData.id || "",
                    name: productData.name || "Unknown Product",
                    description:
                      productData.description || "No description available",
                    price: productData.price || 0,
                  }
                : {
                    id: "",
                    name: "Unknown Product",
                    description: "No description available",
                    price: 0,
                  },
            };
          }) ?? [],
      };

      console.log("Commande complète avec détails:", orderWithDetails);

      setOrder(orderWithDetails);
      setEditedOrder(orderWithDetails);
    } catch (error: any) {
      console.error("Erreur dans fetchOrder:", error);
      setError(
        `Une erreur est survenue lors de la récupération des détails de la commande : ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableProducts = async () => {
    try {
      const { data, error } = await supabase.from("products").select("*");

      if (error) throw error;
      setAvailableProducts(data || []);
    } catch (error) {
      console.error("Error fetching available products:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .order("name");
      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleClientSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClientSelect = (client: Client) => {
    setEditedOrder((prev) => ({
      ...prev!,
      client_id: client.id,
      client_name: client.name,
      client_email: client.email,
      client_phone: client.telephone,
    }));
    setShowClientSearch(false);
    setSearchTerm("");
  };

  const renderClientInfo = () => (
    <div className="col-span-1 md:col-span-2">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
        Client Information
        {editMode && !showClientSearch && (
          <button
            onClick={() => setShowClientSearch(true)}
            className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition duration-150 ease-in-out"
          >
            <Edit className="w-4 h-4 inline mr-2" />
            Modify Client
          </button>
        )}
      </h2>
      {editMode && showClientSearch ? (
        <div className="space-y-3">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleClientSearch}
              placeholder="Search clients..."
              className="w-full px-4 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
            />
            <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <ul className="mt-2 max-h-40 overflow-auto border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
            {filteredClients.map((client) => (
              <li
                key={client.id}
                onClick={() => {
                  handleClientSelect(client);
                  setShowClientSearch(false);
                }}
                className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-900 dark:text-white"
              >
                {client.name}
              </li>
            ))}
          </ul>
          <button
            onClick={() => setShowClientSearch(false)}
            className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white rounded-md text-sm font-medium hover:bg-gray-400 dark:hover:bg-gray-500 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="flex items-center text-gray-600 dark:text-gray-300">
            <User className="w-5 h-5 mr-3 text-gray-400" />
            <span className="font-medium mr-2">Name:</span>
            {editedOrder?.client_name}
          </p>
          <p className="flex items-center text-gray-600 dark:text-gray-300">
            <Mail className="w-5 h-5 mr-3 text-gray-400" />
            <span className="font-medium mr-2">Email:</span>
            {editedOrder?.client_email}
          </p>
          <p className="flex items-center text-gray-600 dark:text-gray-300">
            <Phone className="w-5 h-5 mr-3 text-gray-400" />
            <span className="font-medium mr-2">Phone:</span>
            {editedOrder?.client_phone}
          </p>
        </div>
      )}
    </div>
  );

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setEditedOrder((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleSave = async () => {
    if (!editedOrder) return;

    try {
      // Préparer les mises à jour de la commande
      const orderUpdates = {
        status: editedOrder.status,
        delivery_method: editedOrder.delivery_method,
        payment_status: editedOrder.payment_status,
        payment_method: editedOrder.payment_method,
        delivery_address: editedOrder.delivery_address,
        notes: editedOrder.notes,
      };

      // Mettre à jour la commande
      const { error: orderError } = await supabase
        .from("orders")
        .update(orderUpdates)
        .eq("id", editedOrder.id);

      if (orderError) throw orderError;

      // Récupérer tous les produits actuels de la commande
      const { data: currentProducts, error: fetchError } = await supabase
        .from("order_products")
        .select("id")
        .eq("order_id", editedOrder.id);

      if (fetchError) throw fetchError;

      const currentProductIds = currentProducts.map((p) => p.id);

      // Préparer les mises à jour, insertions et suppressions des produits
      const productUpdates = [];
      const productInserts = [];
      const productIdsToKeep: string[] = [];

      for (const product of editedOrder.products) {
        if (product.id) {
          productUpdates.push({
            id: product.id,
            order_id: editedOrder.id,
            product_id: product.product_id,
            quantity: product.quantity,
            unit_price: product.unit_price,
          });
          productIdsToKeep.push(product.id);
        } else {
          productInserts.push({
            order_id: editedOrder.id,
            product_id: product.product_id,
            quantity: product.quantity,
            unit_price: product.unit_price,
          });
        }
      }

      // Identifier les produits à supprimer
      const productIdsToDelete = currentProductIds.filter(
        (id) => !productIdsToKeep.includes(id)
      );

      // Mettre à jour les produits existants
      if (productUpdates.length > 0) {
        const { error: updateError } = await supabase
          .from("order_products")
          .upsert(productUpdates);

        if (updateError) throw updateError;
      }

      // Insérer les nouveaux produits
      if (productInserts.length > 0) {
        const { error: insertError } = await supabase
          .from("order_products")
          .insert(productInserts);

        if (insertError) throw insertError;
      }

      // Supprimer les produits qui ne sont plus dans la commande
      if (productIdsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from("order_products")
          .delete()
          .in("id", productIdsToDelete);

        if (deleteError) throw deleteError;
      }

      // Récupérer la commande mise à jour avec toutes les informations nécessaires
      const { data: updatedOrderData, error: fetchUpdatedError } =
        await supabase
          .from("orders")
          .select(
            `
          *,
          clients (
            name,
            email,
            telephone
          ),
          products:order_products (
            id,
            product_id,
            quantity,
            unit_price,
            total_price,
            product:products (
              id,
              name,
              description,
              price
            )
          )
        `
          )
          .eq("id", editedOrder.id)
          .single();

      if (fetchUpdatedError) throw fetchUpdatedError;

      if (!updatedOrderData) {
        throw new Error("Impossible de récupérer les données mises à jour");
      }

      // Mise à jour de l'état avec les nouvelles données
      const updatedOrder = {
        ...updatedOrderData,
        client_name: updatedOrderData.clients?.name || "Client inconnu",
        client_email: updatedOrderData.clients?.email || "Email non disponible",
        client_phone:
          updatedOrderData.clients?.telephone || "Téléphone non disponible",
      };

      setOrder(updatedOrder);
      setEditedOrder(updatedOrder);
      setEditMode(false);
      updatedOrder.total_amount = calculateTotalAmount(updatedOrder.products);
      console.log("Mise à jour réussie");
    } catch (error: any) {
      console.error("Erreur lors de la mise à jour de la commande:", error);
      setError(
        `Une erreur est survenue lors de la mise à jour de la commande : ${
          error.message || "Erreur inconnue"
        }`
      );
    }
  };

  const handleCancel = () => {
    setEditedOrder(order);
    setEditMode(false);
  };

  const handleAddProduct = async () => {
    if (!selectedProduct || !editedOrder) return;

    const productToAdd = availableProducts.find(
      (p) => p.id === selectedProduct
    );
    if (!productToAdd) return;

    const newOrderProduct: OrderProduct = {
      id: "", // Sera généré par la base de données
      product_id: productToAdd.id,
      quantity: quantity,
      unit_price: productToAdd.price,
      total_price: quantity * productToAdd.price, // Calculé côté front-end
      product: productToAdd,
    };

    try {
      // Insérer le nouveau produit dans la base de données
      const { data, error } = await supabase
        .from("order_products")
        .insert({
          order_id: editedOrder.id,
          product_id: newOrderProduct.product_id,
          quantity: newOrderProduct.quantity,
          unit_price: newOrderProduct.unit_price,
          // Ne pas envoyer total_price au back-end
        })
        .select();

      if (error) throw error;

      if (data && data[0]) {
        // Mettre à jour l'état local avec le nouveau produit
        const insertedProduct = {
          ...newOrderProduct,
          id: data[0].id,
          // Garder le total_price calculé côté front-end
        };

        setEditedOrder((prev) => ({
          ...prev!,
          products: [...prev!.products, insertedProduct],
          total_amount: calculateTotalAmount([
            ...prev!.products,
            insertedProduct,
          ]),
        }));

        setSelectedProduct("");
        setQuantity(1);
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout du produit à la commande:", error);
      // Gérer l'erreur (par exemple, afficher un message à l'utilisateur)
    }
  };

  const handleRemoveProduct = (productId: string) => {
    if (!editedOrder) return;
    console.log("Removing product with ID:", productId);
    const updatedProducts = editedOrder.products.filter(
      (p) => p.id !== productId
    );
    console.log("Updated products:", updatedProducts);
    setEditedOrder((prev) => ({
      ...prev!,
      products: updatedProducts,
      total_amount: calculateTotalAmount(updatedProducts),
    }));
  };

  const calculateTotalAmount = (products: OrderProduct[]): number => {
    return products.reduce((total, product) => total + product.total_price, 0);
  };

  const renderOrderDetails = () => (
    <div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Order Details
      </h2>
      <div className="space-y-3 text-sm">
        <p className="flex items-center text-gray-600 dark:text-gray-300">
          <Calendar className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Order Date:</span>
          {editedOrder?.created_at
            ? new Date(editedOrder.created_at).toLocaleDateString()
            : "N/A"}
        </p>
        <p className="flex items-center text-gray-600 dark:text-gray-300">
          <Truck className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Delivery Method:</span>
          {editMode ? (
            <select
              name="delivery_method"
              value={editedOrder?.delivery_method}
              onChange={handleInputChange}
              className={inputClass}
            >
              <option value="Livraison">Livraison</option>
              <option value="Envoi">Envoi</option>
              <option value="Retrait">Retrait</option>
            </select>
          ) : (
            editedOrder?.delivery_method
          )}
        </p>
        <p className="flex items-center text-gray-600 dark:text-gray-300">
          <CreditCard className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Payment Method:</span>
          {editMode ? (
            <select
              name="payment_method"
              value={editedOrder?.payment_method}
              onChange={handleInputChange}
              className={inputClass}
            >
              <option value="Carte de crédit">Carte de crédit</option>
              <option value="PayPal">PayPal</option>
              <option value="Virement bancaire">Virement bancaire</option>
              <option value="Espèces">Espèces</option>
              <option value="Revolut">Revolut</option>
            </select>
          ) : (
            editedOrder?.payment_method
          )}
        </p>
        <p className="flex items-center text-gray-600 dark:text-gray-300">
          <DollarSign className="w-5 h-5 mr-3 text-gray-400" />
          <span className="font-medium mr-2">Payment Status:</span>
          {editMode ? (
            <select
              name="payment_status"
              value={editedOrder?.payment_status}
              onChange={handleInputChange}
              className={inputClass}
            >
              <option value="Non payé">Non payé</option>
              <option value="Payé">Payé</option>
              <option value="Remboursé">Remboursé</option>
              <option value="En attente de paiement">
                En attente de paiement
              </option>
            </select>
          ) : (
            editedOrder?.payment_status
          )}
        </p>
      </div>
    </div>
  );

  const openInWaze = () => {
    if (editedOrder?.delivery_address) {
      setIsDropdownOpen(false);
      window.open(`https://waze.com/ul?q=${encodeURIComponent(editedOrder.delivery_address)}`, '_blank');
    }
  };

  const openInGoogleMaps = () => {
    if (editedOrder?.delivery_address) {
      setIsDropdownOpen(false);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(editedOrder.delivery_address)}`, '_blank');
    }
  };

  const renderDeliveryAddress = () => (
    <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700 relative">
      {!editMode && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
            <MapPin className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400" />
            Delivery Address
          </h2>
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition duration-150 ease-in-out flex items-center"
            >
              Open in Map
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                  <button
                    onClick={openInWaze}
                    className=" px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full text-left flex items-center"
                    role="menuitem"
                  >
                    <Navigation className="w-4 h-4 mr-2" /> Open in Waze
                  </button>
                  <button
                    onClick={openInGoogleMaps}
                    className=" px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full text-left flex items-center"
                    role="menuitem"
                  >
                    <Map className="w-4 h-4 mr-2" /> Open in Google Maps
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col">
        <div className="flex items-start mb-4">
          {editMode ? (
            <div className="flex-1">
              <DeliveryAddress
                order={editedOrder!}
                handleInputChange={handleInputChange}
              />
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300">
              <b>Adresse :</b> {editedOrder?.delivery_address || "No address provided"}
            </p>
          )}
        </div>
        {!editMode && editedOrder?.delivery_address && (
          <div className="h-64 w-full rounded-lg overflow-hidden mt-4">
            <MapComponent address={editedOrder.delivery_address} />
          </div>
        )}
      </div>
    </div>
  );

  const renderOrderItems = () => (
    <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Order Items
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Product
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Quantity
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Unit Price
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Total
              </th>
              {editMode && (
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
            {editedOrder?.products.map((product) => (
              <tr
                key={product.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {product.product?.name || "Unknown Product"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {editMode ? (
                    <input
                      type="number"
                      value={product.quantity}
                      onChange={(e) => {
                        const newQuantity = parseInt(e.target.value);
                        setEditedOrder((prev) => ({
                          ...prev!,
                          products: prev!.products.map((p) =>
                            p.id === product.id
                              ? {
                                  ...p,
                                  quantity: newQuantity,
                                  total_price: newQuantity * p.unit_price,
                                }
                              : p
                          ),
                          total_amount: calculateTotalAmount(prev!.products),
                        }));
                      }}
                      className={inputClass + " w-20"}
                      min="1"
                    />
                  ) : (
                    product.quantity
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {product.unit_price.toFixed(2)} €
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {product.total_price.toFixed(2)} €
                </td>
                {editMode && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleRemoveProduct(product.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editMode && (
        <div className="mt-4 flex items-center space-x-4">
          <select
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            className={inputClass + " flex-1"}
          >
            <option value="">Select a product</option>
            {availableProducts.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value))}
            className={inputClass + " w-20"}
            min="1"
          />
          <button
            onClick={handleAddProduct}
            className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 transition duration-150 ease-in-out"
          >
            <PlusCircle className="w-5 h-5 inline mr-2" />
            Add Product
          </button>
        </div>
      )}
    </div>
  );

  const renderNotes = () => (
    <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Notes
      </h2>
      {editMode ? (
        <textarea
          name="notes"
          value={editedOrder?.notes || ""}
          onChange={handleInputChange}
          className={inputClass + " w-full p-4"}
          rows={4}
        />
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
          {editedOrder?.notes || "No notes available."}
        </p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center p-4 bg-red-100 rounded-lg m-4">
        {error}
      </div>
    );
  }

  if (!order || !editedOrder) {
    return (
      <div className="text-center p-4 bg-yellow-100 text-yellow-700 rounded-lg m-4">
        Order not found
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg max-h-48">
      <div className=" mx-auto bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-auto">
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <button
              onClick={() => navigate("/dashboard/orders")}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition duration-150 ease-in-out mb-4 md:mb-0"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to orders
            </button>
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Commande #{editedOrder?.order_number}
            </h1>
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <span className="px-4 py-2 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm font-medium">
                {editMode ? (
                  <select
                    name="status"
                    value={editedOrder?.status}
                    onChange={handleInputChange}
                    className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm font-medium"
                  >
                    <option value="En attente">En attente</option>
                    <option value="En cours">En cours</option>
                    <option value="Terminée">Terminée</option>
                    <option value="Expédié">Expédié</option>
                    <option value="Annulée">Annulée</option>
                  </select>
                ) : (
                  editedOrder?.status
                )}
              </span>
              {editMode ? (
                <div className="space-x-2">
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-slate-600 text-white rounded-md text-sm font-medium hover:bg-black transition duration-150 ease-in-out"
                  >
                    <Save className="w-4 h-4 inline mr-2" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-slate-400 text-white rounded-md text-sm font-medium hover:bg-slate-500 transition duration-150 ease-in-out"
                  >
                    <X className="w-4 h-4 inline mr-2" />
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition duration-150 ease-in-out"
                >
                  <Edit className="w-4 h-4 inline mr-2" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Client and Order Info */}
        <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {renderClientInfo()}
          {renderOrderDetails()}
        </div>

        {/* Delivery Address */}
        {renderDeliveryAddress()}

        {/* Order Items */}
        {renderOrderItems()}

        {/* Order Total */}
        <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex justify-end">
            <div className="w-full max-w-md">
              <div className="flex justify-between py-2 text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">Subtotal</span>
                <span>{editedOrder?.total_amount.toFixed(2) ?? "0.00"} €</span>
              </div>
              <div className="flex justify-between py-2 text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">Shipping</span>
                <span>Included</span>
              </div>
              <div className="flex justify-between py-4 text-lg font-bold text-gray-900 dark:text-white border-t border-gray-200 dark:border-gray-700 mt-2">
                <span>Total</span>
                <span>{editedOrder?.total_amount.toFixed(2) ?? "0.00"} €</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {renderNotes()}
      </div>
    </div>
  );
}
